import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  LayoutDashboard, ListTodo, Sparkles, BarChart3, Wallet, Compass, Settings,
  ArrowLeft, ArrowRight, Beaker, Send, Loader2, CheckCircle2, Save, Bug,
  Lightbulb, FileText, ChevronRight, Trash2, Circle, Download, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { tl } from '../../lib/testingLabApi';

const ICONS = {
  LayoutDashboard, ListTodo, Sparkles, BarChart3, Wallet, Compass, Settings,
};

const STATUS_META = {
  new: { label: 'New', color: 'bg-violet-500/15 text-violet-700 border-violet-300' },
  submitted: { label: 'Submitted', color: 'bg-blue-500/15 text-blue-700 border-blue-300' },
  partially_resolved: { label: 'Partially Resolved', color: 'bg-amber-500/15 text-amber-700 border-amber-300' },
  confirmed_deployed: { label: 'Confirmed Deployed', color: 'bg-emerald-500/15 text-emerald-700 border-emerald-300' },
};

const SEVERITY_COLOR = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-400 text-slate-900',
  low: 'bg-slate-300 text-slate-700',
};

const uid = () => `ws_${Math.random().toString(36).slice(2, 10)}`;

export default function WizardTab() {
  const [view, setView] = useState('launcher'); // 'launcher' | 'runner' | 'reports'
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);

  useEffect(() => {
    tl.wizardModules().then(d => setModules(d.items || [])).catch(e => toast.error(e.message));
  }, []);

  const openModule = async (moduleId) => {
    try {
      const mod = await tl.wizardModuleDetail(moduleId);
      setActiveModule(mod);
      const first = mod.blocks.find(b => b.level === 'overview') || mod.blocks[0];
      setActiveBlockId(first?.block_id || null);
      setView('runner');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="wizard-tab">
      {/* Sub-nav */}
      <div className="flex items-center gap-2 mb-4" data-testid="wizard-subnav">
        <Button
          size="sm"
          variant={view === 'launcher' ? 'default' : 'outline'}
          onClick={() => setView('launcher')}
          data-testid="wizard-subnav-launcher"
        >
          <Beaker className="w-3.5 h-3.5 mr-1.5" /> Test Modules
        </Button>
        <Button
          size="sm"
          variant={view === 'reports' ? 'default' : 'outline'}
          onClick={() => { setView('reports'); setReportsRefreshKey(k => k + 1); }}
          data-testid="wizard-subnav-reports"
        >
          <FileText className="w-3.5 h-3.5 mr-1.5" /> Wizard Reports
        </Button>
        {view === 'runner' && activeModule && (
          <Badge variant="secondary" className="ml-2" data-testid="wizard-active-module-badge">
            Testing: {activeModule.title}
          </Badge>
        )}
      </div>

      {view === 'launcher' && <WizardLauncher modules={modules} onOpen={openModule} />}
      {view === 'runner' && activeModule && (
        <WizardRunner
          module={activeModule}
          activeBlockId={activeBlockId}
          setActiveBlockId={setActiveBlockId}
          onSavedReport={() => { setReportsRefreshKey(k => k + 1); }}
          onExit={() => setView('launcher')}
          onGoToReports={() => { setView('reports'); setReportsRefreshKey(k => k + 1); }}
        />
      )}
      {view === 'reports' && <WizardReports refreshKey={reportsRefreshKey} />}
    </div>
  );
}

// ===== Launcher =====================================================
function WizardLauncher({ modules, onOpen }) {
  return (
    <div className="space-y-4" data-testid="wizard-launcher">
      <Card className="p-4 bg-gradient-to-br from-violet-50 via-indigo-50 to-white dark:from-violet-950/30 dark:via-indigo-950/20 dark:to-transparent border-violet-200 dark:border-violet-800">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center shrink-0">
            <Beaker className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Testing Wizard</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pick a module → the wizard walks you through logical blocks (high-level first, then detail).
              Type messy notes — the <strong>Bug &amp; Enhancement Reporting</strong> capability
              structures them into proper reports with file, function, and route context auto-captured.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wizard-module-grid">
        {modules.map(m => {
          const Icon = ICONS[m.icon] || Beaker;
          return (
            <Card
              key={m.module_id}
              className="p-4 hover:border-violet-400 hover:shadow-md transition-all cursor-pointer flex flex-col gap-3"
              onClick={() => onOpen(m.module_id)}
              data-testid={`wizard-module-card-${m.module_id}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-violet-700 dark:text-violet-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{m.title}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">{m.route}</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{m.tagline}</p>
              <div className="flex items-center justify-between mt-auto">
                <span className="text-[10px] text-muted-foreground">
                  {m.block_count} blocks · {m.overview_block_count} overview
                </span>
                <Button size="sm" data-testid={`wizard-test-now-${m.module_id}`}>
                  Test now <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ===== Runner =======================================================
function WizardRunner({ module, activeBlockId, setActiveBlockId, onSavedReport, onExit, onGoToReports }) {
  const blocks = module.blocks || [];
  const tree = useMemo(() => buildBlockTree(blocks), [blocks]);
  const activeBlock = blocks.find(b => b.block_id === activeBlockId);

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="wizard-runner">
      {/* Left: block tree */}
      <Card className="col-span-12 lg:col-span-4 p-3 max-h-[calc(100vh-260px)] overflow-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onExit} data-testid="wizard-runner-back">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Modules
            </Button>
          </div>
          <Badge variant="outline" className="text-[10px]">{blocks.length} blocks</Badge>
        </div>
        <h3 className="font-semibold text-sm mb-0.5">{module.title}</h3>
        <p className="text-xs text-muted-foreground mb-3">{module.tagline}</p>
        <div className="space-y-0.5" data-testid="wizard-block-tree">
          {tree.map(node => (
            <BlockTreeNode
              key={node.block.block_id}
              node={node}
              activeBlockId={activeBlockId}
              onPick={setActiveBlockId}
              depth={0}
            />
          ))}
        </div>
      </Card>

      {/* Right: block + chat */}
      <div className="col-span-12 lg:col-span-8 space-y-3">
        {activeBlock ? (
          <BlockChatPanel
            key={activeBlock.block_id}
            moduleId={module.module_id}
            moduleTitle={module.title}
            block={activeBlock}
            onSavedReport={onSavedReport}
            onGoToReports={onGoToReports}
          />
        ) : (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            Pick a block on the left to start testing.
          </Card>
        )}
      </div>
    </div>
  );
}

function buildBlockTree(blocks) {
  const byId = Object.fromEntries(blocks.map(b => [b.block_id, { block: b, children: [] }]));
  const roots = [];
  blocks.forEach(b => {
    if (b.parent_id && byId[b.parent_id]) byId[b.parent_id].children.push(byId[b.block_id]);
    else roots.push(byId[b.block_id]);
  });
  return roots;
}

function BlockTreeNode({ node, activeBlockId, onPick, depth }) {
  const { block, children } = node;
  const active = block.block_id === activeBlockId;
  return (
    <div>
      <button
        type="button"
        onClick={() => onPick(block.block_id)}
        className={`w-full text-left px-2 py-1.5 rounded-md flex items-start gap-1.5 text-xs transition-colors ${active ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-900 dark:text-violet-100 font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        data-testid={`wizard-block-node-${block.block_id}`}
      >
        <span className="mt-0.5">
          {block.level === 'overview' ? <Circle className="w-2.5 h-2.5 fill-violet-500 stroke-violet-500" />
            : block.level === 'feature' ? <Circle className="w-2.5 h-2.5 fill-indigo-300 stroke-indigo-400" />
              : <Circle className="w-2.5 h-2.5 stroke-slate-400" />}
        </span>
        <span className="flex-1">
          <span className="truncate block">{block.title}</span>
          {active && block.description && (
            <span className="block text-[10px] font-normal text-muted-foreground mt-0.5">{block.description}</span>
          )}
        </span>
      </button>
      {children.length > 0 && (
        <div className="ml-1">
          {children.map(c => (
            <BlockTreeNode key={c.block.block_id} node={c} activeBlockId={activeBlockId} onPick={onPick} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function BlockChatPanel({ moduleId, moduleTitle, block, onSavedReport, onGoToReports }) {
  const [sessionId] = useState(() => uid());
  const [conversation, setConversation] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null); // latest AI-proposed report
  const [saving, setSaving] = useState(false);

  // seed conversation with prompts
  useEffect(() => {
    setConversation([]);
    setDraft(null);
  }, [block.block_id]);

  const send = useCallback(async (msg) => {
    const text = (msg ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    const nextConv = [...conversation, { role: 'user', content: text }];
    setConversation(nextConv);
    setInput('');
    try {
      const result = await tl.wizardChat({
        module_id: moduleId,
        block_id: block.block_id,
        session_id: sessionId,
        message: text,
        conversation: nextConv,
      });
      setConversation(c => [...c, { role: 'assistant', content: result.reply || '(no reply)' }]);
      if (result.report_ready && result.report) setDraft(result.report);
      else if (result.report) setDraft(result.report); // keep a live draft even if not finalized
    } catch (e) {
      toast.error(e.message);
      setConversation(c => [...c, { role: 'assistant', content: `Error: ${e.message}` }]);
    } finally {
      setSending(false);
    }
  }, [input, conversation, sending, moduleId, block.block_id, sessionId]);

  const saveReport = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await tl.wizardCreateReport({
        module_id: moduleId,
        block_id: block.block_id,
        kind: draft.kind || 'bug',
        title: draft.title || 'Untitled',
        severity: draft.severity || 'medium',
        summary: draft.summary || '',
        observed_behavior: draft.observed_behavior || '',
        expected_behavior: draft.expected_behavior || '',
        reproduction_steps: draft.reproduction_steps || [],
        acceptance_criteria: draft.acceptance_criteria || [],
        suspected_root_cause: draft.suspected_root_cause || '',
        related_files: draft.related_files || block.frontend_files || [],
        related_functions: draft.related_functions || block.key_functions || [],
        related_route: draft.related_route || block.route || null,
        tags: draft.tags || [],
        conversation,
      });
      toast.success(`Report saved: ${saved.title}`);
      setDraft(null);
      setConversation([]);
      onSavedReport?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Block context panel */}
      <Card className="p-3" data-testid="wizard-block-context">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              <span>{moduleTitle}</span>
              <ChevronRight className="w-3 h-3" />
              <span>{block.level}</span>
            </div>
            <h4 className="font-semibold text-sm mt-1">{block.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">{block.description}</p>
          </div>
          <Badge variant="outline" className="text-[10px] font-mono shrink-0">{block.route || '—'}</Badge>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3 text-[11px]">
          <MetaList label="Frontend files" items={block.frontend_files} />
          <MetaList label="Backend files" items={block.backend_files} />
          <MetaList label="Key functions" items={block.key_functions} />
          <MetaList label="Must work" items={block.must_work} />
        </div>

        {block.prompts && block.prompts.length > 0 && (
          <div className="mt-3 border-t pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Starter questions</div>
            <div className="flex flex-wrap gap-1.5">
              {block.prompts.map((p, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant="outline"
                  className="h-auto py-1 px-2 text-[11px] whitespace-normal text-left"
                  onClick={() => send(p)}
                  disabled={sending}
                  data-testid={`wizard-starter-${i}`}
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Chat */}
      <Card className="p-0 flex flex-col" style={{ minHeight: 320 }} data-testid="wizard-chat">
        <div className="px-3 py-2 border-b flex items-center gap-2 text-xs font-semibold">
          <Sparkles className="w-3.5 h-3.5 text-violet-600" />
          Bug &amp; Enhancement Reporting
          <span className="text-[10px] text-muted-foreground font-normal ml-auto">GPT-4o</span>
        </div>
        <div className="flex-1 overflow-auto p-3 space-y-2 max-h-[40vh]" data-testid="wizard-chat-log">
          {conversation.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-8">
              Drop your messy notes below or click a starter question. The AI will structure them into a bug or enhancement report.
            </div>
          )}
          {conversation.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={`max-w-[85%] text-xs px-3 py-2 rounded-lg ${m.role === 'user' ? 'bg-violet-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="text-xs px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> thinking…
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-2 flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
            placeholder="Describe what you saw, what you expected, any messy details…  (⌘/Ctrl+Enter to send)"
            rows={2}
            className="text-xs min-h-[48px] resize-none"
            data-testid="wizard-chat-input"
          />
          <Button onClick={() => send()} disabled={!input.trim() || sending} size="sm" data-testid="wizard-chat-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </Card>

      {/* Draft preview + save */}
      {draft && (
        <Card className="p-3 border-violet-300 bg-violet-50/50 dark:bg-violet-950/20" data-testid="wizard-draft-preview">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              {draft.kind === 'enhancement' ? <Lightbulb className="w-4 h-4 text-amber-500" /> : <Bug className="w-4 h-4 text-red-500" />}
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                Proposed {draft.kind || 'bug'} report
              </span>
              <Badge className={`${SEVERITY_COLOR[draft.severity] || ''} text-[10px] uppercase`}>{draft.severity || 'medium'}</Badge>
            </div>
            <Button size="sm" onClick={saveReport} disabled={saving} data-testid="wizard-save-report">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save report
            </Button>
          </div>
          <h5 className="font-semibold text-sm">{draft.title}</h5>
          {draft.summary && <p className="text-xs text-muted-foreground mt-1">{draft.summary}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-[11px]">
            {draft.observed_behavior && <Field label="Observed" value={draft.observed_behavior} />}
            {draft.expected_behavior && <Field label="Expected" value={draft.expected_behavior} />}
            {draft.suspected_root_cause && <Field label="Suspected root cause" value={draft.suspected_root_cause} />}
            <MetaList label="Reproduction" items={draft.reproduction_steps} />
            <MetaList label="Acceptance" items={draft.acceptance_criteria} />
            <MetaList label="Related files" items={draft.related_files} />
            <MetaList label="Related functions" items={draft.related_functions} />
          </div>
        </Card>
      )}
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function MetaList({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">{label}</div>
      <ul className="space-y-0.5 list-disc list-inside marker:text-slate-400">
        {items.map((it, i) => <li key={i} className="font-mono text-[11px] leading-snug">{it}</li>)}
      </ul>
    </div>
  );
}

// ===== Reports table ================================================
function WizardReports({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', module: '', kind: '' });
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await tl.wizardListReports({
        status: filter.status,
        module_id: filter.module,
        kind: filter.kind,
      });
      setRows(d.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Prune selections that no longer match current filter results.
  useEffect(() => {
    setSelected(prev => {
      const visible = new Set(rows.map(r => r.wizard_report_id));
      const next = new Set();
      prev.forEach(id => { if (visible.has(id)) next.add(id); });
      return next;
    });
  }, [rows]);

  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const allVisibleSelected = rows.length > 0 && rows.every(r => selected.has(r.wizard_report_id));
  const someVisibleSelected = rows.some(r => selected.has(r.wizard_report_id)) && !allVisibleSelected;
  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.wizard_report_id)));
  };

  const selectAllBy = (predicate) => {
    setSelected(new Set(rows.filter(predicate).map(r => r.wizard_report_id)));
  };

  const updateStatus = async (id, status) => {
    try {
      await tl.wizardUpdateReportStatus(id, status);
      toast.success('Status updated');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await tl.wizardDeleteReport(id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} report${selected.size === 1 ? '' : 's'}?`)) return;
    try {
      await Promise.all([...selected].map(id => tl.wizardDeleteReport(id)));
      toast.success(`Deleted ${selected.size} report${selected.size === 1 ? '' : 's'}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e.message); }
  };

  const bulkStatusUpdate = async (status) => {
    if (selected.size === 0) return;
    try {
      await Promise.all([...selected].map(id => tl.wizardUpdateReportStatus(id, status)));
      toast.success(`Updated ${selected.size} report${selected.size === 1 ? '' : 's'} → ${STATUS_META[status]?.label || status}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const doExport = async (format) => {
    if (selected.size === 0) return;
    setExporting(true);
    try {
      // Preserve visible order (table order) for the export
      const orderedIds = rows.filter(r => selected.has(r.wizard_report_id)).map(r => r.wizard_report_id);
      const { blob, filename, count } = await tl.wizardExportReports(orderedIds, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Exported ${count} report${count === 1 ? '' : 's'} as ${format.toUpperCase()}`);
    } catch (e) { toast.error(e.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const counts = useMemo(() => {
    const c = { new: 0, submitted: 0, partially_resolved: 0, confirmed_deployed: 0 };
    rows.forEach(r => { c[r.wizard_status] = (c[r.wizard_status] || 0) + 1; });
    return c;
  }, [rows]);

  const selectionStats = useMemo(() => {
    const picked = rows.filter(r => selected.has(r.wizard_report_id));
    const bugs = picked.filter(r => r.kind === 'bug').length;
    const enhancements = picked.filter(r => r.kind === 'enhancement').length;
    return { total: picked.length, bugs, enhancements };
  }, [rows, selected]);

  return (
    <div className="space-y-3" data-testid="wizard-reports">
      {/* Status summary strip */}
      <div className="grid grid-cols-4 gap-2" data-testid="wizard-reports-summary">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <Card
            key={key}
            className={`p-2 cursor-pointer transition-all ${filter.status === key ? 'ring-2 ring-violet-500' : ''}`}
            onClick={() => setFilter(f => ({ ...f, status: f.status === key ? '' : key }))}
            data-testid={`wizard-reports-summary-${key}`}
          >
            <div className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-md inline-block border ${meta.color}`}>
              {meta.label}
            </div>
            <div className="text-2xl font-bold mt-1">{counts[key] || 0}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Input
          placeholder="module_id filter (e.g. daily_dashboard)"
          value={filter.module}
          onChange={(e) => setFilter(f => ({ ...f, module: e.target.value }))}
          className="max-w-xs text-xs"
          data-testid="wizard-reports-filter-module"
        />
        <Select value={filter.kind || 'all'} onValueChange={(v) => setFilter(f => ({ ...f, kind: v === 'all' ? '' : v }))}>
          <SelectTrigger className="max-w-[140px] text-xs" data-testid="wizard-reports-filter-kind"><SelectValue placeholder="All kinds" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="bug">Bugs</SelectItem>
            <SelectItem value="enhancement">Enhancements</SelectItem>
          </SelectContent>
        </Select>
        {(filter.status || filter.module || filter.kind) && (
          <Button size="sm" variant="ghost" onClick={() => setFilter({ status: '', module: '', kind: '' })} data-testid="wizard-reports-clear-filter">
            Clear
          </Button>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="text-xs" data-testid="wizard-reports-quickselect">
                Quick select
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-[10px]">Select visible rows…</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => selectAllBy(() => true)} data-testid="wizard-reports-quickselect-all">All visible</DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectAllBy(r => r.kind === 'bug')} data-testid="wizard-reports-quickselect-bugs">All bugs</DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectAllBy(r => r.kind === 'enhancement')} data-testid="wizard-reports-quickselect-enh">All enhancements</DropdownMenuItem>
              <DropdownMenuSeparator />
              {Object.entries(STATUS_META).map(([k, m]) => (
                <DropdownMenuItem key={k} onClick={() => selectAllBy(r => r.wizard_status === k)} data-testid={`wizard-reports-quickselect-${k}`}>
                  Status: {m.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setSelected(new Set())} data-testid="wizard-reports-quickselect-clear">Clear selection</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Selection / export toolbar */}
      {selected.size > 0 && (
        <Card
          className="p-2 flex items-center gap-3 border-violet-300 bg-violet-50/70 dark:bg-violet-950/20"
          data-testid="wizard-reports-selection-toolbar"
        >
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold orka-text-primary" data-testid="wizard-reports-selection-count">
              {selectionStats.total} selected
            </span>
            <span className="orka-text-tertiary">
              ({selectionStats.bugs} bug{selectionStats.bugs === 1 ? '' : 's'}, {selectionStats.enhancements} enh)
            </span>
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="text-xs" data-testid="wizard-reports-bulk-status">
                  Set status
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <DropdownMenuItem key={k} onClick={() => bulkStatusUpdate(k)} data-testid={`wizard-reports-bulk-status-${k}`}>{m.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button size="sm" variant="ghost" className="text-xs text-red-600 hover:text-red-700" onClick={bulkDelete} data-testid="wizard-reports-bulk-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="text-xs" disabled={exporting} data-testid="wizard-reports-export">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-[10px]">Aggregate {selectionStats.total} report{selectionStats.total === 1 ? '' : 's'} as…</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => doExport('md')} data-testid="wizard-reports-export-md">Markdown (.md)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport('txt')} data-testid="wizard-reports-export-txt">Plain text (.txt)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport('docx')} data-testid="wizard-reports-export-docx">Word document (.docx)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport('pdf')} data-testid="wizard-reports-export-pdf">PDF (.pdf)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-xs" data-testid="wizard-reports-table">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 w-8">
                  <Checkbox
                    checked={allVisibleSelected}
                    data-state={someVisibleSelected ? 'indeterminate' : undefined}
                    onCheckedChange={toggleAll}
                    data-testid="wizard-reports-select-all"
                  />
                </th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Module · Block</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Severity</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-6 text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No reports yet — start a wizard to create one.</td></tr>
              ) : rows.map(r => {
                const isSelected = selected.has(r.wizard_report_id);
                return (
                <tr
                  key={r.wizard_report_id}
                  className={`border-t hover:bg-slate-50 dark:hover:bg-slate-900/30 ${isSelected ? 'bg-violet-50/60 dark:bg-violet-950/20' : ''}`}
                  data-testid={`wizard-report-row-${r.wizard_report_id}`}
                  data-selected={isSelected ? 'true' : 'false'}
                >
                  <td className="px-3 py-2 align-top">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(r.wizard_report_id)}
                      data-testid={`wizard-report-select-${r.wizard_report_id}`}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium">{r.title}</div>
                    {r.summary && <div className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">{r.summary}</div>}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div>{r.module_title || r.module_id}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{r.block_title}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span className="inline-flex items-center gap-1">
                      {r.kind === 'enhancement' ? <Lightbulb className="w-3 h-3 text-amber-500" /> : <Bug className="w-3 h-3 text-red-500" />}
                      {r.kind}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Badge className={`${SEVERITY_COLOR[r.severity] || ''} text-[10px] uppercase`}>{r.severity}</Badge>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Select value={r.wizard_status} onValueChange={(v) => updateStatus(r.wizard_report_id, v)}>
                      <SelectTrigger className="h-7 text-[11px] min-w-[160px]" data-testid={`wizard-report-status-${r.wizard_report_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, m]) => (
                          <SelectItem key={k} value={k}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 align-top text-[10px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString()}
                    <div>{r.created_by_email || ''}</div>
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <Button size="sm" variant="ghost" onClick={() => del(r.wizard_report_id)} data-testid={`wizard-report-delete-${r.wizard_report_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
